import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';

import { CheckboxValue, FieldChangeTrigger } from '../../enums/form';
import type { FieldChangeTriggerHandler } from '../../types/form';
import { getHTMLElementName } from '../../utils/form/name';
import Checkbox from '../Checkbox';
import { Flex } from '../Flex';
import StateMessages from '../Input/StatefulInput/StateMessages';
import type { InputStateMessage } from '../Input/StatefulInput/types';

export interface CheckboxFieldProps<FieldName extends string = string> {
  label: string | ReactNode;
  name: FieldName;
  disabled?: boolean;
  value: CheckboxValue;
  errors?: string[];
  warnings?: string[];
  successes?: string[];
  messages?: InputStateMessage[];
  onChange: FieldChangeTriggerHandler<FieldName>;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  nameSuffix?: string;
  eventName?: string;
  className?: string;
  withMessagesIcon?: boolean;
}

const UnstyledCheckboxWrapper = styled(Flex)``;

const CheckboxWrapper = (props: ComponentProps<typeof UnstyledCheckboxWrapper>) => (
  <UnstyledCheckboxWrapper width={1} {...props} />
);

const CheckboxField = ({
  label,
  name,
  errors = [],
  warnings = [],
  successes = [],
  messages = [],
  value,
  onChange,
  onClick,
  disabled,
  nameSuffix,
  eventName,
  className,
  withMessagesIcon,
}: CheckboxFieldProps) => {
  const isChecked = value === CheckboxValue.True;
  const stateMessagesProps = {
    errors,
    warnings,
    successes,
    messages,
    withIcon: withMessagesIcon,
  };

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { checked } = target;
    onChange({
      fieldName: name,
      value: checked ? CheckboxValue.True : CheckboxValue.False,
      trigger: FieldChangeTrigger.Change,
    });
  };

  const hasError = errors?.length > 0;

  return (
    <CheckboxWrapper className={className}>
      <Checkbox
        name={getHTMLElementName(name, nameSuffix)}
        label={label}
        checked={isChecked}
        onChange={handleChange}
        disabled={disabled}
        eventName={eventName}
        hasError={hasError}
        {...(onClick ? { onClick } : {})}
      />
      <StateMessages {...stateMessagesProps} />
    </CheckboxWrapper>
  );
};

export default CheckboxField;
